import {
  ANALYTICS_DELTA_BEHAVIORS,
  ANALYTICS_METRIC_SEMANTICS,
  ANALYTICS_SUMMARY_BEHAVIORS,
  ANALYTICS_TIME_BEHAVIORS,
  ANALYTICS_VISUAL_TYPES,
  createAnalyticsLibraryCategory,
  createAnalyticsLibraryEntry,
  createAnalyticsSectionLibrary
} from '../../visuals/visualDataModel';
import { ANALYTICS_GRAPH_TYPES } from '../../visuals/graphCatalog';
import { ANALYTICS_FILTER_KEYS, ANALYTICS_GROUP_BY_OPTIONS, ANALYTICS_GROUP_BY_KEYS } from '../../analyticsFilters';

const semantics = (metric, time, summary, delta = ANALYTICS_DELTA_BEHAVIORS.NONE) => ({
  metric,
  time,
  summary,
  delta
});

const SCREEN_FILTERS = [
  ANALYTICS_FILTER_KEYS.PERIOD,
  ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  ANALYTICS_FILTER_KEYS.ROLES,
  ANALYTICS_FILTER_KEYS.RECRUITERS,
  ANALYTICS_FILTER_KEYS.SOURCES,
  ANALYTICS_FILTER_KEYS.TAGS,
  ANALYTICS_FILTER_KEYS.LOCATIONS
];

const SOURCE_COMPARISON_FILTERS = [
  ANALYTICS_FILTER_KEYS.PERIOD,
  ANALYTICS_FILTER_KEYS.DEPARTMENTS,
  ANALYTICS_FILTER_KEYS.ROLES,
  ANALYTICS_FILTER_KEYS.RECRUITERS,
  ANALYTICS_FILTER_KEYS.TAGS,
  ANALYTICS_FILTER_KEYS.LOCATIONS
];

const recruitmentAnalyticsLibrary = createAnalyticsSectionLibrary({
  section: 'recruitment',
  categories: [
    createAnalyticsLibraryCategory({
      id: 'funnel_conversion',
      label: 'Funnel & Conversion',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'stage_conversion_waterfall',
          label: 'Stage conversion waterfall',
          category: 'funnel_conversion',
          visualType: ANALYTICS_VISUAL_TYPES.FUNNEL,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW,
          defaultSize: 'large',
          defaultVariant: 'data-forward',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COUNT,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SOURCE_COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['period', 'department', 'role', 'recruiter', 'source', 'tag', 'location'],
            measures: ['stage_volume', 'drop_off_count', 'drop_off_rate', 'conversion_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'drop_off_breakdown',
          label: 'Drop-off breakdown',
          category: 'funnel_conversion',
          visualType: ANALYTICS_VISUAL_TYPES.MATRIX,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.STACKED_BAR,
            ANALYTICS_GRAPH_TYPES.HEATMAP,
            ANALYTICS_GRAPH_TYPES.TABLE
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.STACKED_BAR,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COMPOSITION,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SOURCE_COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['stage', 'exit_reason', 'period'],
            measures: ['candidate_count', 'drop_off_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'application_to_hire_conversion_rate',
          label: 'Application-to-hire conversion',
          category: 'funnel_conversion',
          visualType: ANALYTICS_VISUAL_TYPES.KPI,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.KPI_CARD,
            ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME,
            ANALYTICS_GRAPH_TYPES.GAUGE
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.KPI_CARD,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RATE,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: SOURCE_COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['period'],
            measures: ['conversion_rate', 'applications', 'accepted']
          }
        }),
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'time_velocity',
      label: 'Time & Velocity',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'time_to_hire_trend',
          label: 'Time-to-hire trend',
          category: 'time_velocity',
          visualType: ANALYTICS_VISUAL_TYPES.TIME_SERIES,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.LINE_OVER_TIME,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.DURATION,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.AVG_DURATION,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: SOURCE_COMPARISON_FILTERS,
          dataShape: {
            dimensions: ['period'],
            measures: ['average_time_to_hire', 'delta']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'time_to_hire_by_segment',
          label: 'Time-to-hire by segment',
          category: 'time_velocity',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.HORIZONTAL_BAR],
          defaultGraph: ANALYTICS_GRAPH_TYPES.HORIZONTAL_BAR,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.DURATION,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.AVG_DURATION
          ),
          supportedFilters: SCREEN_FILTERS,
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
          groupByOptions: ANALYTICS_GROUP_BY_OPTIONS,
          graphConfig: {
            [ANALYTICS_GRAPH_TYPES.HORIZONTAL_BAR]: {
              displayMetricId: 'averageDays',
              primaryMetricId: 'averageDays'
            }
          },
          dataShape: {
            dimensions: ['segment'],
            measures: ['average_time_to_hire']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'time_to_hire_components',
          label: 'Time-to-hire components',
          category: 'time_velocity',
          visualType: ANALYTICS_VISUAL_TYPES.METRIC_ROWS,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.TABLE],
          defaultGraph: ANALYTICS_GRAPH_TYPES.TABLE,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.DURATION,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.AVG_DURATION,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: SCREEN_FILTERS,
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
          groupByOptions: ANALYTICS_GROUP_BY_OPTIONS,
          graphConfig: {
            [ANALYTICS_GRAPH_TYPES.TABLE]: {
              displayMetricId: 'averageDays',
              contextMetricIds: ['latestMonth', 'share']
            }
          },
          dataShape: {
            dimensions: ['role_type', 'stage_component', 'period'],
            measures: ['days_in_component']
          }
        }),
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'source_effectiveness',
      label: 'Source Effectiveness',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'source_quality_matrix',
          label: 'Source quality matrix',
          category: 'source_effectiveness',
          visualType: ANALYTICS_VISUAL_TYPES.MATRIX,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE],
          defaultGraph: ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['source'],
            measures: ['volume', 'conversion_rate', 'time_to_hire', 'offer_acceptance_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'source_rankings',
          label: 'Source rankings',
          category: 'source_effectiveness',
          visualType: ANALYTICS_VISUAL_TYPES.COMPARISON,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.SOURCE_RANKINGS],
          defaultGraph: ANALYTICS_GRAPH_TYPES.SOURCE_RANKINGS,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['source', 'period'],
            measures: ['application_volume', 'hire_count', 'conversion_rate', 'applications_per_hire', 'mom_growth']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'source_trend_over_time',
          label: 'Source trend over time',
          category: 'source_effectiveness',
          visualType: ANALYTICS_VISUAL_TYPES.TIME_SERIES,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.MULTI_LINE],
          defaultGraph: ANALYTICS_GRAPH_TYPES.MULTI_LINE,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RATE,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.CURRENT_RATE,
            ANALYTICS_DELTA_BEHAVIORS.MOM
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['source', 'month'],
            measures: ['volume', 'conversion_rate']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'source_to_stage_breakdown',
          label: 'Source-to-stage breakdown',
          category: 'source_effectiveness',
          visualType: ANALYTICS_VISUAL_TYPES.MATRIX,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.HEATMAP],
          defaultGraph: ANALYTICS_GRAPH_TYPES.HEATMAP,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COMPOSITION,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['source', 'furthest_stage', 'period'],
            measures: ['candidate_count']
          }
        }),
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'offer_intelligence',
      label: 'Offer Intelligence',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'offer_waterfall',
          label: 'Offer waterfall',
          category: 'offer_intelligence',
          visualType: ANALYTICS_VISUAL_TYPES.OFFER_COLUMNS,
          supportedGraphs: [ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW],
          defaultGraph: ANALYTICS_GRAPH_TYPES.PIPELINE_FLOW,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COUNT,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['offer_stage'],
            measures: ['offer_count']
          }
        }),
        createAnalyticsLibraryEntry({
          id: 'offer_risk_summary',
          label: 'Offer risk summary',
          category: 'offer_intelligence',
          visualType: ANALYTICS_VISUAL_TYPES.KPI,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.OFFER_SIGNAL_CARD
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.OFFER_SIGNAL_CARD,
          defaultSize: 'medium',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.COMPOSITION,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['period', 'decline_reason'],
            measures: ['decline_rate', 'expiry_rate', 'decline_count']
          }
        })
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'hiring_goals_actuals',
      label: 'Hiring Goals vs Actuals',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'headcount_targets_by_tag_group',
          label: 'Headcount targets by tag group',
          category: 'hiring_goals_actuals',
          visualType: ANALYTICS_VISUAL_TYPES.PROGRESS,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.GOAL_TRACK
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.GOAL_TRACK,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.GOAL,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.GOAL_PROGRESS
          ),
          supportedFilters: SCREEN_FILTERS,
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
          groupByOptions: ANALYTICS_GROUP_BY_OPTIONS,
          dataShape: {
            dimensions: ['tag_group', 'period'],
            measures: ['target', 'actual', 'in_pipeline', 'percent_complete', 'days_remaining']
          }
        })
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'geography',
      label: 'Geography',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'applicant_traffic_map',
          label: 'Applicant traffic map',
          category: 'geography',
          visualType: ANALYTICS_VISUAL_TYPES.GEO_TRAFFIC,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH,
            ANALYTICS_GRAPH_TYPES.REGIONAL_MAP,
            ANALYTICS_GRAPH_TYPES.CITY_DOT_MAP
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.WORLD_CHOROPLETH,
          defaultSize: 'large',
          defaultVariant: 'data-forward',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.GEO,
            ANALYTICS_TIME_BEHAVIORS.ROLLING_WINDOW,
            ANALYTICS_SUMMARY_BEHAVIORS.GEO_ROLLUP
          ),
          supportedFilters: SCREEN_FILTERS,
          dataShape: {
            dimensions: ['country', 'region', 'city'],
            measures: ['application_volume', 'conversion_rate', 'hire_count']
          }
        })
      ]
    }),
    createAnalyticsLibraryCategory({
      id: 'job_posting_performance',
      label: 'Job Posting Performance',
      widgets: [
        createAnalyticsLibraryEntry({
          id: 'job_posting_performance_matrix',
          label: 'Job posting performance matrix',
          category: 'job_posting_performance',
          visualType: ANALYTICS_VISUAL_TYPES.MATRIX,
          supportedGraphs: [
            ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE,
            ANALYTICS_GRAPH_TYPES.TABLE
          ],
          defaultGraph: ANALYTICS_GRAPH_TYPES.CONDITIONAL_TABLE,
          defaultSize: 'large',
          dataSemantics: semantics(
            ANALYTICS_METRIC_SEMANTICS.RELATIONSHIP,
            ANALYTICS_TIME_BEHAVIORS.PERIOD_TREND,
            ANALYTICS_SUMMARY_BEHAVIORS.WINDOW_TOTAL
          ),
          supportedFilters: SCREEN_FILTERS,
          supportsGroupBy: true,
          defaultGroupBy: ANALYTICS_GROUP_BY_KEYS.DEPARTMENT,
          groupByOptions: ANALYTICS_GROUP_BY_OPTIONS,
          dataShape: {
            dimensions: ['posting', 'period'],
            measures: ['views', 'apply_rate', 'time_to_first_application', 'source_quality']
          }
        })
      ]
    })
  ]
});

export default recruitmentAnalyticsLibrary;
